import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import type {Swiper as SwiperInstance} from 'swiper';
import s from '../../s';
import ModalSimple from '../../../global/modal-simple';
import {Swiper, SwiperSlide} from 'swiper/react';
import 'swiper/css';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import {Keyboard, Scrollbar, Navigation, Zoom} from 'swiper/modules';
import {Portal} from '../../../util';
import {CUSTOMER_PHOTOS_MIN_COUNT, getCustomerPhotoAccessibleName} from './customer-photos/customer-photos-util';

const resetSwiperScroll = (swiperEl: HTMLElement | null) => {
    if (!swiperEl) {
        return;
    }
    if (swiperEl.scrollLeft !== 0) {
        swiperEl.scrollLeft = 0;
    }
    if (swiperEl.scrollTop !== 0) {
        swiperEl.scrollTop = 0;
    }
};

const SliderRealImages: React.FC = observer(() => {
    const [zoomIndex, setZoomIndex] = useState<number>(-1);
    const openerRef = useRef<HTMLButtonElement | null>(null);
    const focusGuardCleanupRef = useRef<(() => void) | null>(null);

    const restoreOpenerFocus = () => {
        const opener = openerRef.current;
        if (!opener) {
            return;
        }
        // Defer until after ModalSimple / site modal teardown.
        window.setTimeout(() => {
            opener.focus({preventScroll: true});
        }, 0);
    };

    const closeZoom = () => {
        s.ui.zoom_real_images = false;
        restoreOpenerFocus();
    };

    useEffect(() => {
        const handleModalClosed = (e: Event) => {
            const detail = (e as CustomEvent).detail;
            if (detail !== 'modal-prod-swiper-real') {
                return;
            }
            s.ui.zoom_real_images = false;
            const opener = openerRef.current;
            if (opener) {
                window.setTimeout(() => {
                    opener.focus({preventScroll: true});
                }, 0);
            }
        };

        document.addEventListener('modal-closed', handleModalClosed);

        return () => {
            document.removeEventListener('modal-closed', handleModalClosed);
            focusGuardCleanupRef.current?.();
            focusGuardCleanupRef.current = null;
        };
    }, []);

    /**
     * Tab focus runs browser scroll-into-view on the Swiper root (scrollLeft), which
     * shifts slides while absolute nav arrows stay put. Reset scroll, and if the
     * focused slide is off-screen let Swiper move to it instead.
     *
     * Important: do not use React onFocus* on <Swiper> — any `on*` prop is treated as an
     * internal Swiper event and never reaches the DOM.
     */
    const bindFocusScrollGuard = (swiper: SwiperInstance) => {
        focusGuardCleanupRef.current?.();

        const swiperEl = swiper.el;
        if (!swiperEl) {
            focusGuardCleanupRef.current = null;
            return;
        }

        const onFocusIn = (e: FocusEvent) => {
            const target = e.target;
            if (!(target instanceof HTMLElement)) {
                return;
            }
            const btn = target.closest('button.prod-real-images-thumb');
            if (!btn || !swiperEl.contains(btn) || swiper.destroyed) {
                return;
            }

            const slide = btn.closest('.swiper-slide');
            if (slide instanceof HTMLElement && !slide.classList.contains('swiper-slide-visible')) {
                const realIndexAttr = slide.getAttribute('data-swiper-slide-index');
                const realIndex = realIndexAttr != null ? Number(realIndexAttr) : NaN;
                if (!Number.isNaN(realIndex) && realIndex >= 0) {
                    swiper.slideToLoop(realIndex);
                }
            }

            const reset = () => resetSwiperScroll(swiperEl);
            reset();
            window.requestAnimationFrame(reset);
        };

        swiperEl.addEventListener('focusin', onFocusIn);
        focusGuardCleanupRef.current = () => {
            swiperEl.removeEventListener('focusin', onFocusIn);
        };
    };

    const openZoom = (i: number, opener: HTMLButtonElement) => {
        openerRef.current = opener;
        setZoomIndex(i);
        s.ui.zoom_real_images = true;
    };

    if (!s.ready || !s.prod || !s.prod.real_images || s.prod.real_images.length < CUSTOMER_PHOTOS_MIN_COUNT) {
        return null;
    }

    const images = s.prod.real_images;

    return (
        <div>
            <Swiper
                id="prod-swiper-real"
                centeredSlides={false}
                grabCursor={true}
                zoom={false}
                loop={true}
                watchSlidesProgress={true}
                keyboard={{enabled: true}}
                scrollbar={false}
                navigation={true}
                spaceBetween={10}
                pagination={false}
                height={125}
                updateOnWindowResize={true}
                slidesPerView={4}
                onSwiper={bindFocusScrollGuard}
                breakpoints={{
                    0: {
                        slidesPerView: 1,
                        slidesPerGroup: 1,
                        centeredSlides: true,
                    },
                    310: {
                        slidesPerView: 2,
                        slidesPerGroup: 2,
                        centeredSlides: true,
                    },
                    420: {
                        slidesPerView: 3,
                        slidesPerGroup: 3,
                        centeredSlides: false,
                    },
                    600: {
                        slidesPerView: 4,
                        slidesPerGroup: 4,
                    },
                    650: {
                        slidesPerView: 5,
                        slidesPerGroup: 5,
                    },
                }}
                modules={[Keyboard, Navigation, Zoom, Scrollbar]}
                className="prod-swiper-real"
                aria-roledescription="carousel"
                role="region"
                aria-label="Real product images"
            >
                {images.map((m, i: number) => {
                    const accessibleName = getCustomerPhotoAccessibleName(m.alt, i + 1);
                    return (
                        <SwiperSlide key={m.id}>
                            <button
                                type="button"
                                className="prod-real-images-thumb"
                                aria-haspopup="dialog"
                                aria-label={accessibleName}
                                onClick={e => openZoom(i, e.currentTarget)}
                            >
                                <picture>
                                    <source
                                        type="image/webp"
                                        srcSet={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th250.webp`}
                                    />
                                    <img
                                        src={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th250.jpg`}
                                        loading="lazy"
                                        alt={accessibleName}
                                        width={125}
                                        height={125}
                                    />
                                </picture>
                            </button>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
            {s.ui.zoom_real_images && (
                <ModalSimple
                    id="modal-prod-swiper-real"
                    title="Product Zoom"
                    open={true}
                    maxWidth={'90%'}
                    close={closeZoom}
                >
                    <Swiper
                        id="prod-swiper-real-zoom"
                        slidesPerView={1}
                        initialSlide={zoomIndex}
                        centeredSlides={true}
                        grabCursor={true}
                        zoom={true}
                        loop={true}
                        keyboard={{
                            enabled: true,
                        }}
                        scrollbar={true}
                        navigation={true}
                        pagination={false}
                        modules={[Keyboard, Scrollbar, Navigation, Zoom]}
                        aria-roledescription="carousel"
                        role="region"
                        aria-label="Real product images"
                    >
                        {images.map((m, i) => {
                            const accessibleName = getCustomerPhotoAccessibleName(m.alt, i + 1);
                            return (
                                <SwiperSlide
                                    key={m.id}
                                    virtualIndex={i}
                                >
                                    <picture className="swiper-zoom-container">
                                        <source
                                            type="image/webp"
                                            srcSet={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th1024.webp`}
                                        />
                                        <img
                                            src={`https://${Portal?.CDNDomain}/images/cached/rp/rp${m.id}_th1024.jpg`}
                                            loading="lazy"
                                            alt={accessibleName}
                                            width={1024}
                                            height={1024}
                                            style={{
                                                maxWidth: window.innerWidth * 0.8,
                                                maxHeight: window.innerHeight * 0.8,
                                            }}
                                        />
                                    </picture>
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                </ModalSimple>
            )}
        </div>
    );
});

export default SliderRealImages;
